import React from 'react';
import styled, { css } from 'styled-components';
import { withCssSelector } from '../../shared/withCssSelector';

// Drawn as a mask, not an <img>, so the glyph takes its colour from the theme
// instead of the fill baked into the SVG.
// eslint-disable-next-line max-len
const checkmarkUrl = 'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'8\' viewBox=\'0 0 12 8\' fill=\'none\'%3E%3Cpath d=\'M11.7489 0.234776C12.0837 0.54731 12.0837 1.05237 11.7489 1.3649L4.89134 7.7656C4.5565 8.07813 4.0154 8.07813 3.68056 7.7656L0.25105 4.56525C-0.0836833 4.25272 -0.0836833 3.74766 0.25105 3.43513C0.585836 3.12259 1.12855 3.12259 1.46339 3.43513L4.26185 6.06791L10.5381 0.234776C10.8729 -0.0782586 11.414 -0.0782586 11.7489 0.234776Z\' fill=\'white\'/%3E%3C/svg%3E';

export const checkboxSizes = {
	large: 'large',
	compact: 'compact',
	min: 'min',
} as const;

export type CheckboxSize = typeof checkboxSizes[keyof typeof checkboxSizes];

// eslint-disable-next-line no-magic-numbers
const boxSize = 16;

const StyledInput = styled.input<{
	$disabled?: boolean | undefined;
	$hasError?: boolean | undefined;
	$size: CheckboxSize;
}>`
	box-sizing: border-box;
	position: relative;
	width: ${boxSize}px;
	height: ${boxSize}px;
	padding: 0;
	// The app stylesheet pushes checkboxes down a few pixels with a type
	// selector, which outweighs a single class. Doubling ours outweighs it back,
	// so the box centres on its label.
	&& {
		margin: 0;
	}
	flex: 0 0 auto;
	border-radius: 2px;
	pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
	border-style: solid;
	border-width: ${props => props.theme.form.checkbox.stroke.rested}px;
	border-color: ${props => props.theme.form.checkbox.default.border};
	appearance: none;
	background-color: ${props => props.theme.form.checkbox.default.background};
	outline: none !important;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;

	// Click target. Vertical only — the row grows above and below the box while
	// the horizontal reach comes from the <label> wrapping it, which is already
	// clickable. Sized off the centre so the border width never eats into it.
	// ponytail: overlaps rows packed closer than the target — space rows by the
	// target size, or wrap in a sized box if that ever bites.
	&::after {
		content: '';
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: ${boxSize}px;
		height: ${props => props.theme.form.checkbox.target[props.$size]}px;
	}

	&:hover {
		border-color: ${props => props.theme.form.checkbox.hover.border};
	}

	&:active {
		border-width: ${props => props.theme.form.checkbox.stroke.pressed}px;
		border-color: ${props => props.theme.form.checkbox.hover.border};
	}

	// Keyboard focus only — a mouse press already shows the pressed stroke.
	&:focus-visible {
		outline: none;
		border-color: ${props => props.theme.form.checkbox.focus.border};
	}

	&:checked,
	&:indeterminate {
		border-color: ${props => props.theme.form.checkbox.checked.border};
		background: ${props => props.theme.form.checkbox.checked.background};

		&:hover {
			border-color: ${props => props.theme.form.checkbox.checked.engaged};
			background: ${props => props.theme.form.checkbox.checked.engaged};
		}

		&:active {
			border-width: ${props => props.theme.form.checkbox.stroke.pressed}px;
			border-color: ${props => props.theme.form.checkbox.checked.engaged};
			background: ${props => props.theme.form.checkbox.checked.engaged};
		}

		&:before {
			content: '';
			background-color: ${props => props.theme.form.checkbox.checked.glyph};
		}

		// The glyph dims with the box it sits on, keeping their contrast steady.
		&:hover:before,
		&:active:before {
			background-color: ${props => props.theme.form.checkbox.checked.engagedGlyph};
		}
	}

	&:checked:before {
		width: 12px;
		height: 8px;
		-webkit-mask: url("${checkmarkUrl}") no-repeat center / contain;
		mask: url("${checkmarkUrl}") no-repeat center / contain;
	}

	// Select-all in a partially selected list. Same box as :checked; a dash
	// instead of the tick.
	&:indeterminate:before {
		width: 10px;
		height: 2px;
		border-radius: 1px;
	}

	${props => props.$hasError && css`
		border-color: ${props.theme.form.input.error.border};
		box-shadow: ${props.theme.form.input.error.shadow};
	`}

	${({ $disabled, theme }) => $disabled && css`
		opacity: ${theme.form.disabled.opacity};
	`}
`;

export type CheckboxProps = {
	hasError?: boolean | undefined,
	indeterminate?: boolean | undefined,
	size?: CheckboxSize | undefined
} & Omit<React.ComponentPropsWithoutRef<'input'>, 'type' | 'size'>

export const Checkbox = withCssSelector(React.forwardRef<HTMLInputElement, CheckboxProps>(
	function Checkbox(
		{
			hasError,
			disabled,
			indeterminate,
			tabIndex,
			readOnly,
			size = checkboxSizes.compact,
			...props
		},
		ref
	) {
		// :indeterminate has no HTML attribute — it only exists as a DOM property.
		const innerRef = React.useRef<HTMLInputElement | null>(null);
		React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);
		React.useEffect(() => {
			if (innerRef.current) {
				innerRef.current.indeterminate = Boolean(indeterminate);
			}
		}, [indeterminate]);

		return <StyledInput
			ref={innerRef}
			type={'checkbox'}
			$hasError={hasError}
			$disabled={disabled}
			$size={size}
			tabIndex={disabled ? -1 : tabIndex}
			readOnly={disabled ? true : readOnly}
			{...props}
		/>;
	}
), StyledInput.toString());
